/**
 * SearchableSelect — single-value searchable select primitive.
 *
 * Adapted from `MultiSelect` (see that file for the original a11y/UX rationale)
 * but for SINGLE selection: no chips, `value` is `string | null`, selecting an
 * option replaces the value (instead of toggling membership in an array) and
 * closes the dropdown. Built for inline cell editors, form fields and dictionary
 * selects that need a one-of-N picker with local search over an already-fetched
 * `options` list.
 *
 * UX (mirrors MultiSelect):
 * - Click trigger → otwiera dropdown z `options`
 * - Type w input → filtruje opcje (search z diakrytyką-insensitive, wspólny
 *   `normalizeSearch` z `lib/search-normalize` co MultiSelect — "wisn"
 *   matchuje "Wiśniewski"); przeszukiwane: `label`, `description`, `searchText`
 * - Click opcji → wybór (onChange(value) + zamknięcie)
 * - Click outside → zamyka dropdown
 * - Klawiatura: Enter/Space/ArrowDown/ArrowUp na triggerze otwiera dropdown; w otwartym panelu
 *   fokus zostaje w polu wyszukiwania, strzałki góra/dół (z zawijaniem) przesuwają aktywną
 *   opcję, Enter wybiera aktywną (wzorzec WAI-ARIA combobox + `aria-activedescendant`)
 * - Esc w polu wyszukiwania / na triggerze / w panelu zamyka TYLKO panel i NIE propaguje do
 *   otaczającego `Modal` (zdarzenia z portalu bąblują przez drzewo Reacta); z pola wyszukiwania
 *   i z panelu fokus wraca na trigger (z przycisku „X" zostaje na „X"), więc kolejny Esc
 *   zamyka modal jak dotąd. Po wyborze opcji (Enter lub klik) fokus też wraca na trigger
 * - Tab w polu wyszukiwania zamyka panel i oddaje fokus triggerowi (bez preventDefault — przeglądarka
 *   przenosi go stamtąd na następny/poprzedni element, więc w `Modal` zostaje w pułapce fokusu;
 *   panel jest w portalu na końcu `body`, więc sam Tab uciekłby poza dokument); opcje mają tabIndex=-1
 * - Aktywna opcja podąża za `options` (start = wybrana wartość), dopóki użytkownik jej nie ruszy
 *   — ważne przy fetch-on-open, gdy opcje przychodzą po otwarciu panelu
 * - `allowClear` → opcjonalny X w triggerze, wywołuje onChange(null) bez otwierania dropdownu
 *   i oddaje fokus triggerowi (X znika po wyczyszczeniu, a fokus nie może wylądować na `body`);
 *   na samym triggerze Delete/Backspace też czyści wartość
 * - `badges` na opcji → małe plakietki po etykiecie (pierwszy wiersz opcji)
 * - `disabled` na opcji → opcja widoczna, ale nie do wyboru (przyciemniona; klik/Enter nic nie robią,
 *   strzałki ją omijają) — np. kandydat zajęty przez inny rekord, z wyjaśnieniem w `description`
 * - Tryb serwerowy: `filterLocally={false}` wyłącza lokalny filtr (lista `options` pochodzi już z
 *   zapytania), a `onSearchChange` oddaje wpisaną frazę rodzicowi (np. do zapytania z debounce);
 *   `loading` pokazuje „Ładowanie…" zamiast „Brak wyników" do czasu odpowiedzi, `emptyMessage`
 *   podmienia tekst pustej listy (np. komunikat o błędzie pobierania), a `selectedLabel` daje
 *   etykietę wybranej wartości, której nie ma w bieżącej (zawężonej) liście
 * - Aktywna opcja (podświetlenie + `aria-activedescendant`) nigdy nie jest opcją `disabled` — przy
 *   otwarciu, zmianie `options` i wpisaniu frazy ląduje na pierwszej dostępnej
 *
 * **Panel dropdownu renderowany PORTALEM do body (position: fixed)** — 1:1 jak MultiSelect
 * (§4.12): inaczej `absolute` w kontenerze ze `overflow` (modal / scroll) byłby przycinany.
 * Pozycja liczona z prostokąta triggera, odświeżana przy scrollu/resize.
 *
 * a11y: outer to `<div role="combobox">` (jak MultiSelect); listbox dropdown
 * z `role="listbox"` + `role="option"` (unikalne `id` = prefiks `useId()` + indeks opcji w
 * pełnej, niefiltrowanej liście `options` — więc po wpisaniu frazy `aria-activedescendant`
 * zmienia wartość, gdy aktywna jest inna opcja) + `aria-selected`; pole wyszukiwania wskazuje
 * aktywną opcję przez `aria-activedescendant`.
 *
 * i18n: stringi user-facing (placeholder, searchPlaceholder, noResults,
 * clearAriaLabel) z `common.searchableSelect.*` per CLAUDE.md §4.5.
 */

import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import { Badge, type BadgeTone } from '@/components/Badge';
import { type AnchoredPanelPosition, computeAnchoredPanelPosition } from '@/lib/anchored-panel';
import { normalizeSearch } from '@/lib/search-normalize';
import { cn } from '@/lib/utils';

/** Ton plakietki przy opcji (mapowany na paletę statusów `Badge`). */
export type SearchableSelectBadgeTone = 'neutral' | 'accent' | 'success' | 'warning';

export interface SearchableSelectBadge {
  label: string;
  /** Domyślnie `neutral`. */
  tone?: SearchableSelectBadgeTone;
}

const BADGE_TONE: Record<SearchableSelectBadgeTone, BadgeTone> = {
  neutral: 'neutral',
  accent: 'accent',
  success: 'success',
  warning: 'warning',
};

export interface SearchableSelectOption {
  /** Unikalna wartość (UUID typowo). */
  value: string;
  /** Wyświetlana etykieta. */
  label: string;
  /** Opcjonalny pomocniczy tekst pod labelem (np. email pod nazwą). */
  description?: string;
  /** Małe plakietki w pierwszym wierszu opcji, zaraz po etykiecie (np. typ, status). */
  badges?: ReadonlyArray<SearchableSelectBadge>;
  /**
   * Dodatkowy tekst uwzględniany w wyszukiwaniu (obok `label` i `description`),
   * diakrytyka-insensitive. Nie jest wyświetlany.
   */
  searchText?: string;
  /** Opcja widoczna, ale niedostępna do wyboru (przyciemniona, pomijana przez strzałki). */
  disabled?: boolean;
}

export interface SearchableSelectProps {
  /** Aktualnie wybrana wartość (controlled), `null` = brak wyboru. */
  value: string | null;
  /** Callback przy zmianie selekcji. */
  onChange: (value: string | null) => void;
  /** Lista wszystkich dostępnych opcji. */
  options: ReadonlyArray<SearchableSelectOption>;
  /** Placeholder gdy nic nie wybrane. Default: `common.searchableSelect.placeholder`. */
  placeholder?: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Gdy true i wartość jest ustawiona — pokazuje X w triggerze, wywołuje onChange(null). */
  allowClear?: boolean;
  /** `id` triggera (combobox) — pozwala wskazać go z `<label htmlFor>`. Domyślnie id z `useId()`. */
  id?: string;
  /**
   * `id` zewnętrznego labela. `<label htmlFor>` na `<div role="combobox">` nie nadaje nazwy
   * dostępnej (element nie jest „labellable" wg HTML), więc nazwę daje to powiązanie ARIA.
   */
  'aria-labelledby'?: string;
  /** Nazwa dostępna triggera (combobox), gdy brak widocznego labela do `aria-labelledby`. */
  'aria-label'?: string;
  /** Klasy dla wrapper'a. */
  className?: string;
  /**
   * Dodatkowe klasy elementu z tekstem wartości (wybrana etykieta albo placeholder) w triggerze —
   * np. wyrównanie wartości w komórce tabeli (`text-right`). Element jest już `flex-1`, więc samo
   * `text-*` wystarcza. Brak = wygląd bez zmian.
   */
  valueClassName?: string;
  /** Test-id na wrapperze + bazowy prefix dla przycisku/dropdownu/opcji wewnątrz. */
  'data-testid'?: string;
  /**
   * Wywoływany przy KAŻDEJ zmianie stanu otwarcia dropdownu — fetch-on-open hook,
   * ten sam wzorzec co MultiSelect (patrz doc tamtego pliku dla pełnego
   * uzasadnienia, czemu nie focus/blur bubbling).
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Wołany przy każdej ZMIANIE frazy w polu wyszukiwania (także z `''` po zamknięciu panelu, o ile
   * fraza nie była już pusta; nie przy montowaniu) — pozwala rodzicowi odpytać serwer. Razem z
   * `filterLocally={false}`.
   */
  onSearchChange?: (search: string) => void;
  /**
   * `false` — `options` są już przefiltrowane po stronie serwera, więc komponent nie filtruje ich
   * drugi raz po wpisaniu frazy. Domyślnie `true` (lokalny filtr po całej liście).
   */
  filterLocally?: boolean;
  /** `true` — lista opcji jest właśnie pobierana (pusta lista pokazuje „Ładowanie…", nie „Brak wyników"). */
  loading?: boolean;
  /**
   * Tekst pokazywany zamiast „Ładowanie…"/„Brak wyników", gdy lista opcji jest pusta (np. błąd
   * pobierania listy z serwera). Brak = dotychczasowe teksty.
   */
  emptyMessage?: string;
  /**
   * Etykieta wybranej wartości, gdy `value` nie występuje w `options` (np. wybrany rekord wypadł z
   * zawężonej serwerowo listy). Bez niej trigger pokazałby placeholder mimo ustawionej wartości.
   */
  selectedLabel?: string;
}

/**
 * Indeks pierwszej opcji NIE-`disabled`, licząc od `from` (włącznie) w kierunku rosnącym, z
 * zawinięciem; `-1`, gdy wszystkie są niedostępne albo lista jest pusta.
 */
function firstEnabledIndex(list: ReadonlyArray<SearchableSelectOption>, from: number): number {
  const count = list.length;
  if (count === 0) return -1;
  const start = Math.min(Math.max(from, 0), count - 1);
  for (let step = 0; step < count; step++) {
    const index = (start + step) % count;
    if (!list[index]?.disabled) return index;
  }
  return -1;
}

/**
 * Indeks startowy aktywnej opcji dla danej listy: wybrana wartość (jeśli jest na liście), inaczej
 * pierwsza; gdy trafia w opcję `disabled` — pierwsza dostępna dalej (z zawinięciem). Gdy wszystkie
 * są niedostępne, zostaje punkt startowy (i tak nie jest podświetlany).
 */
function initialActiveIndex(
  list: ReadonlyArray<SearchableSelectOption>,
  value: string | null,
): number {
  const selectedIndex = list.findIndex((opt) => opt.value === value);
  const start = selectedIndex >= 0 ? selectedIndex : 0;
  const enabled = firstEnabledIndex(list, start);
  return enabled >= 0 ? enabled : start;
}

/** Lokalny filtr opcji po `label`/`description`/`searchText` (fraza już znormalizowana). */
function filterOptionsLocally(
  list: ReadonlyArray<SearchableSelectOption>,
  normalizedPhrase: string,
): ReadonlyArray<SearchableSelectOption> {
  if (!normalizedPhrase) return list;
  return list.filter(
    (opt) =>
      normalizeSearch(opt.label).includes(normalizedPhrase) ||
      (opt.description && normalizeSearch(opt.description).includes(normalizedPhrase)) ||
      (opt.searchText && normalizeSearch(opt.searchText).includes(normalizedPhrase)),
  );
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  allowClear,
  id,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  className,
  valueClassName,
  'data-testid': testIdProp,
  onOpenChange,
  onSearchChange,
  filterLocally = true,
  loading = false,
  emptyMessage,
  selectedLabel,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState<AnchoredPanelPosition | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Przewijanie do aktywnej opcji tylko po akcji z klawiatury / otwarciu — nie po ruchu myszy
  // (inaczej hover „szarpałby" listą). Flaga jest zużywana po KAŻDYM renderze z zamontowanym
  // panelem, więc nie zostaje uzbrojona, gdy aktywna opcja się nie zmieniła.
  const scrollToActiveRef = useRef(false);
  // Czy użytkownik sam ruszył aktywną opcję (strzałki, wpisanie w search, ruch myszy) od
  // otwarcia panelu. Dopóki nie — aktywna opcja podąża za `options` (fetch-on-open).
  const userMovedRef = useRef(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = useId();
  const optionIdPrefix = useId();
  const testId = testIdProp ?? 'searchable-select';

  // Notify the caller of every open-state transition — see MultiSelect's
  // `onOpenChange` doc for why this replaces focus/blur bubbling. Callback
  // captured through a ref so the effect depends ONLY on `isOpen`.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  // Fraza do rodzica (tryb serwerowy) — efekt na `search` łapie WSZYSTKIE ścieżki zmiany, także
  // zerowanie przy zamknięciu panelu (klik na zewnątrz, Esc, wybór). Callback przez ref. Porównanie
  // z poprzednią frazą (a nie flaga „pierwszy przebieg") — nie woła rodzica przy montowaniu, także
  // gdy efekty biegną podwójnie (StrictMode), i nie powtarza `''`, gdy fraza nic się nie zmieniła.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  const lastNotifiedSearchRef = useRef(search);
  useEffect(() => {
    if (lastNotifiedSearchRef.current === search) return;
    lastNotifiedSearchRef.current = search;
    onSearchChangeRef.current?.(search);
  }, [search]);

  const selectedOption = useMemo(
    () => (value !== null ? (options.find((opt) => opt.value === value) ?? null) : null),
    [options, value],
  );

  const normalizedSearch = search && filterLocally ? normalizeSearch(search) : '';
  const filteredOptions = useMemo(
    () => filterOptionsLocally(options, normalizedSearch),
    [options, normalizedSearch],
  );

  // `id` opcji = prefiks + jej indeks w PEŁNEJ liście `options` (nie w przefiltrowanej): po
  // wpisaniu frazy ta sama pozycja na liście bywa już inną opcją, a niezmienione
  // `aria-activedescendant` nie zostałoby ogłoszone przez czytnik ekranu. Id nie zawiera
  // `value` (mogą to być dowolne kody) i jest unikalne w obrębie instancji.
  const optionSourceIndex = useMemo(
    () => new Map(options.map((opt, index) => [opt.value, index] as const)),
    [options],
  );
  function optionDomId(optValue: string): string {
    return `${optionIdPrefix}-${optionSourceIndex.get(optValue) ?? -1}`;
  }

  // Aktywna opcja (podświetlenie + aria-activedescendant) — liczona z `activeIndex` względem
  // AKTUALNEJ listy, więc zawężenie filtra nigdy nie zostawia indeksu poza zakresem.
  const effectiveActiveIndex =
    filteredOptions.length === 0
      ? -1
      : Math.min(Math.max(activeIndex, 0), filteredOptions.length - 1);
  // Opcja `disabled` nigdy nie jest aktywna (też, gdy wszystkie są niedostępne) — inaczej
  // `aria-activedescendant` wskazywałby opcję `aria-disabled`.
  const candidateOption =
    effectiveActiveIndex >= 0 ? filteredOptions[effectiveActiveIndex] : undefined;
  const activeOption = candidateOption && !candidateOption.disabled ? candidateOption : undefined;
  const activeOptionId = activeOption ? optionDomId(activeOption.value) : undefined;

  function openPanel() {
    // Wyszukiwanie jest wtedy puste (zamknięcie zawsze je czyści), więc indeks w `options` =
    // indeks w liście panelu.
    setActiveIndex(initialActiveIndex(options, value));
    userMovedRef.current = false;
    scrollToActiveRef.current = true;
    setIsOpen(true);
  }

  function closePanel() {
    setIsOpen(false);
    setSearch('');
  }

  function toggleOpen() {
    if (isOpen) closePanel();
    else openPanel();
  }

  function handleSelect(optValue: string) {
    onChange(optValue);
    closePanel();
    // Panel (a z nim opcja/pole wyszukiwania z fokusem) znika — fokus wraca na trigger, żeby
    // klawiatura (np. Esc zamykający otaczający Modal) działała dalej, także po wyborze myszą.
    comboboxRef.current?.focus();
  }

  function handleClear(event: React.MouseEvent) {
    event.stopPropagation();
    onChange(null);
    // „X" znika po wyczyszczeniu (`value` → null), a razem z nim fokus — bez tego spadłby na
    // `body` i Esc w otaczającym `Modal` przestałby go zamykać.
    comboboxRef.current?.focus();
  }

  function moveActive(direction: 1 | -1) {
    const count = filteredOptions.length;
    if (count === 0) return;
    userMovedRef.current = true;
    scrollToActiveRef.current = true;
    // Opcje `disabled` są pomijane (z zawijaniem); gdy wszystkie są niedostępne — zostajemy.
    let next = effectiveActiveIndex;
    for (let step = 0; step < count; step++) {
      next = (next + direction + count) % count;
      if (!filteredOptions[next]?.disabled) {
        setActiveIndex(next);
        return;
      }
    }
  }

  function closeAndFocusTrigger() {
    closePanel();
    comboboxRef.current?.focus();
  }

  // Klawisze w polu wyszukiwania — fokus zostaje w inpucie, nawigacja po opcjach przez
  // aria-activedescendant.
  function handleSearchKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter') {
      // Enter potwierdzający kompozycję IME (keyCode 229 = zdarzenie „Process" w przeglądarkach,
      // które nie ustawiają `isComposing` na ostatnim keydown) nie może wybierać opcji.
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      const active = effectiveActiveIndex >= 0 ? filteredOptions[effectiveActiveIndex] : undefined;
      if (!active || active.disabled) return;
      handleSelect(active.value);
    } else if (event.key === 'Tab') {
      // Panel jest w portalu na KOŃCU `body`, więc domyślny Tab z pola wyszukiwania wyprowadziłby
      // fokus poza dokument i ominął pułapkę fokusu otaczającego `Modal` (ta porównuje tylko
      // elementy wewnątrz panelu modala). Dlatego najpierw oddajemy fokus triggerowi — BEZ
      // preventDefault, żeby przeglądarka sama przeniosła go stamtąd na następny/poprzedni element.
      closePanel();
      comboboxRef.current?.focus();
    } else if (event.key === 'Escape') {
      // Panel jest w portalu, ale zdarzenia React bąblują przez drzewo komponentów — bez
      // stopPropagation Esc zamknąłby także otaczający Modal.
      event.preventDefault();
      event.stopPropagation();
      closeAndFocusTrigger();
    }
  }

  // Siatka bezpieczeństwa: Esc z dowolnego miejsca WEWNĄTRZ panelu (np. po kliknięciu opcji
  // lub paska przewijania fokus bywa poza polem wyszukiwania) zamyka tylko panel.
  function handlePanelKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    closeAndFocusTrigger();
  }

  // Pozycja portalowanego panelu — liczona z prostokąta triggera; odświeżana przy
  // scrollu/resize (panel `fixed` nie jedzie z treścią, więc trzeba go przesuwać).
  useEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos(computeAnchoredPanelPosition(r));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  // Przewiń aktywną opcję w widok (tylko po akcji z klawiatury / otwarciu) i ZAWSZE zużyj
  // flagę po renderze z panelem — bez deps, żeby nie została uzbrojona, gdy id aktywnej opcji
  // się nie zmieniło (wpisanie przy aktywnej pierwszej opcji, strzałka na liście 1-elementowej),
  // bo wtedy najbliższy hover przewijałby listę.
  // KOLEJNOŚĆ MA ZNACZENIE: ten efekt musi być zadeklarowany PRZED efektem „podąża za `options`"
  // poniżej. W jednym commicie efekty biegną w kolejności deklaracji, a ten widzi id aktywnej
  // opcji z renderu tego commita; gdyby biegł po efekcie uzbrajającym flagę, przewinąłby do
  // STAREJ opcji i zużył flagę, a render z nowym indeksem już by nic nie przewinął.
  const panelMounted = isOpen && menuPos !== null;
  useEffect(() => {
    if (!panelMounted) return;
    if (scrollToActiveRef.current && activeOptionId) {
      const el = document.getElementById(activeOptionId);
      // jsdom nie implementuje scrollIntoView (w testach daje go globalny stub z `test-setup.ts`).
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    }
    scrollToActiveRef.current = false;
  });

  // Aktywna opcja podąża za `options`, dopóki użytkownik jej nie ruszył: przy fetch-on-open
  // (np. ExtFieldValueInput) opcje przychodzą PO otwarciu panelu, więc start z indeksu
  // wyliczonego w `openPanel` byłby pustą listą → 0 zamiast wybranej wartości. Uzbraja flagę
  // przewijania; samo przewinięcie zrobi efekt powyżej w NASTĘPNYM commicie (po `setActiveIndex`).
  useEffect(() => {
    if (!isOpen || userMovedRef.current || search) return;
    const next = initialActiveIndex(options, value);
    if (next === activeIndex) return;
    scrollToActiveRef.current = true;
    setActiveIndex(next);
  }, [options, isOpen, value, search, activeIndex]);

  // Aktywna opcja stoi na opcji `disabled` PO ruchu użytkownika (wpisanie frazy ustawia 0, nowa
  // odpowiedź serwera podmienia listę pod aktywną) — przesuń ją na pierwszą dostępną, żeby Enter
  // nie był martwy mimo dostępnych opcji. Przed ruchem rządzi efekt wyżej (`initialActiveIndex`
  // już omija `disabled`) — oba naraz ustawiałyby różne indeksy w jednym commicie.
  useEffect(() => {
    if (!isOpen || !candidateOption?.disabled) return;
    if (!userMovedRef.current && !search) return;
    const next = firstEnabledIndex(filteredOptions, effectiveActiveIndex);
    if (next < 0 || next === activeIndex) return;
    scrollToActiveRef.current = true;
    setActiveIndex(next);
  }, [isOpen, candidateOption, filteredOptions, effectiveActiveIndex, activeIndex, search]);

  // Close on click outside — panel jest w PORTALU (poza wrapperRef), więc klik w opcję
  // liczyłby się jako „na zewnątrz" i zamykał dropdown przed rejestracją kliknięcia —
  // dlatego wykluczamy też panelRef (mirror MultiSelect).
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as globalThis.Node;
      const inWrapper = wrapperRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inWrapper && !inPanel) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape niezależnie od miejsca fokusu (też na `<body>` po kliknięciu w pasek
  // przewijania panelu). Faza przechwytywania + `preventDefault`: okno nakładkowe (`Overlay`) —
  // także jego awaryjny nasłuch dla `<body>` — widzi Esc jako już obsłużony i nie zamyka się
  // razem z panelem. Fokus wraca na trigger TUTAJ, w samym nasłuchu: nasłuch w fazie
  // przechwytywania biegnie przed handlerami React (`handleSearchKey`/`handlePanelKey`), a w
  // prawdziwej przeglądarce panel bywa już odmontowany, zanim zdarzenie do nich dotrze — wtedy
  // fokus spadłby na `<body>`. Handlery React robią to samo, więc zwrot fokusu jest idempotentny.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setIsOpen(false);
      setSearch('');
      comboboxRef.current?.focus();
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  function handleTriggerKey(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    // Esc przy otwartym panelu zamyka tylko panel — także gdy fokus jest na „X" w triggerze.
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      closePanel();
      return;
    }
    // Klawisze z potomków (przycisk „X") zostają ich — inaczej Enter/Space na „X" bąbelkuje tu,
    // dostaje preventDefault (nie odpala click) i przełącza panel zamiast czyścić wartość.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOpen();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) openPanel();
    } else if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      allowClear &&
      value !== null
    ) {
      event.preventDefault();
      onChange(null);
    }
  }

  const resolvedPlaceholder = placeholder ?? t('common.searchableSelect.placeholder');

  return (
    <div ref={wrapperRef} className={`relative ${className ?? ''}`} data-testid={testId}>
      {/*
       * a11y: outer to combobox div (jak MultiSelect) — aria-controls linkuje
       * trigger ↔ listbox (WAI-ARIA 1.2 combobox pattern).
       */}
      <div
        ref={comboboxRef}
        id={inputId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && toggleOpen()}
        onKeyDown={handleTriggerKey}
        className={`flex min-h-[42px] w-full cursor-pointer items-center gap-1 rounded-lg border border-border-primary bg-bg-primary px-2 py-1.5 text-left transition-colors hover:border-accent-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200 dark:focus:ring-accent-800 ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        } ${isOpen ? 'border-accent-500' : ''}`}
        data-testid={`${testId}-button`}
      >
        {selectedOption || (value !== null && selectedLabel) ? (
          <span className={cn('flex-1 truncate text-sm text-text-primary', valueClassName)}>
            {selectedOption?.label ?? selectedLabel}
          </span>
        ) : (
          <span className={cn('flex-1 truncate text-sm text-text-secondary', valueClassName)}>
            {resolvedPlaceholder}
          </span>
        )}
        {allowClear && value !== null && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('common.searchableSelect.clearAriaLabel')}
            className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center hover:text-red-500"
            data-testid={`${testId}-clear`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 flex-shrink-0 text-text-secondary transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              // Wysokość docięta do miejsca w oknie (panel scrolluje w środku) — zamiast
              // twardego `max-h-64`, który przy polu na dole formularza wychodził za ekran.
              maxHeight: menuPos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-y-auto rounded-lg border border-border-primary bg-bg-primary shadow-lg"
            onKeyDown={handlePanelKey}
            data-testid={`${testId}-dropdown`}
          >
            {/*
             * Search input poza role="listbox" (WAI-ARIA APG: listbox akceptuje
             * tylko option/group children).
             */}
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                // Start od pierwszej opcji; gdy jest `disabled`, efekt naprawczy przesunie aktywną
                // na pierwszą dostępną (bez drugiego przebiegu filtra przy każdym klawiszu).
                setActiveIndex(0);
                userMovedRef.current = true;
                scrollToActiveRef.current = true;
              }}
              onKeyDown={handleSearchKey}
              placeholder={t('common.searchableSelect.searchPlaceholder')}
              aria-label={t('common.searchableSelect.searchPlaceholder')}
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              // `z-10`: opcja `disabled` ma `opacity-60` (własna warstwa rysowania) — bez tego
              // przewijana pod przyklejonym polem przebijałaby przez nie.
              className="sticky top-0 z-10 w-full border-b border-border-primary bg-bg-primary px-3 py-2 text-sm focus:outline-none"
              autoFocus
              data-testid={`${testId}-search`}
            />
            <div
              id={listboxId}
              role="listbox"
              aria-labelledby={ariaLabelledBy}
              aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? resolvedPlaceholder)}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-secondary">
                  {emptyMessage ??
                    (loading
                      ? t('common.searchableSelect.loading')
                      : t('common.searchableSelect.noResults'))}
                </div>
              ) : (
                filteredOptions.map((opt, index) => {
                  const isSelected = value === opt.value;
                  const isActive = index === effectiveActiveIndex && !opt.disabled;
                  return (
                    <button
                      key={opt.value}
                      id={optionDomId(opt.value)}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={opt.disabled || undefined}
                      // Poza kolejnością tabulacji — nawigacja po opcjach idzie przez
                      // aria-activedescendant z pola wyszukiwania; klik myszą działa jak dotąd.
                      tabIndex={-1}
                      onClick={() => {
                        if (opt.disabled) return;
                        handleSelect(opt.value);
                      }}
                      // Hover ustawia aktywną opcję, żeby mysz i klawiatura nie walczyły o
                      // podświetlenie (mousemove, nie mouseenter — ten drugi odpala też przy
                      // przewijaniu listy pod nieruchomym kursorem).
                      onMouseMove={() => {
                        if (opt.disabled) return;
                        userMovedRef.current = true;
                        scrollToActiveRef.current = false;
                        if (!isActive) setActiveIndex(index);
                      }}
                      className={cn(
                        'flex w-full scroll-mt-10 items-start gap-2 px-3 py-2 text-left text-sm',
                        opt.disabled && 'cursor-not-allowed opacity-60',
                        isActive
                          ? isSelected
                            ? 'bg-accent-100 dark:bg-accent-900/40'
                            : 'bg-bg-secondary'
                          : isSelected && 'bg-accent-50 dark:bg-accent-900/20',
                      )}
                      data-testid={`${testId}-option-${opt.value}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-text-primary">{opt.label}</span>
                          {opt.badges?.map((badge, badgeIndex) => (
                            <Badge
                              key={`${badgeIndex}-${badge.label}`}
                              tone={BADGE_TONE[badge.tone ?? 'neutral']}
                              className="rounded-full px-2 py-px text-[11px]"
                            >
                              {badge.label}
                            </Badge>
                          ))}
                        </div>
                        {opt.description && (
                          <div className="line-clamp-2 text-xs text-text-secondary">
                            {opt.description}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
